import { useCallback, useEffect, useState } from "react";
import { createContainer } from "unstated-next";
import { AxiosError } from "axios";
import { initializeApp } from "firebase/app";
import {
    AuthCredential,
    GoogleAuthProvider,
    OAuthProvider,
    SAMLAuthProvider,
    getAuth,
    linkWithCredential,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithCustomToken,
    signOut,
    deleteUser,
    updatePassword,
    User as FirebaseUser,
    UserCredential
} from "firebase/auth";

import { StatisticsContainer } from "../statistics/statistics.container";
import {
    confirmMicrosoftDirectory,
    precheckSignIn,
    redeemSupportSession
} from "../../../api/admin/tenant/endpoints-referrers";
import { useFirebaseToken } from "./use-firebase-token.util";
import { firebaseConfig } from "../../utilities/constants/constants";
import { useTimer } from "../../utilities/timer/use-timer.util";
import { isAuthDomain } from "../../utilities/routes/routes.util";
import { isAxios } from "../../utilities/errors/errors.util";
import {
    isRefusedFirstSignIn,
    toPolicyRefusalError
} from "../../utilities/tenant-auth/policy-refusal.util";
import {
    setDataToLocalStorage,
    removeDataToLocalStorage
} from "../../utilities/localstorage-dealer/localstorage-setter.util";
import { AuthEvents } from "../../enums/auth-events.enum";
import { CompanyType } from "../../enums/company-type.enum";
import { FirebaseErrorCode } from "../../enums/firebase-error-code.enum";
import { UserRole } from "../../enums/user-role.enum";
import { SsoProviderOption } from "../../interfaces/admin/tenant.interface";
import { User } from "../../interfaces/comps/user.interface";
import { KnowledgeBase } from "../../interfaces/admin/knowledge-base.interface";
import { Config } from "../../interfaces/comps/config.interface";
import { KBConfig } from "../../interfaces/admin/knowledge-base-config.interface";
import { ACCESS_TOKEN_KEY } from "../../../api/base";
import {
    fetchVersion,
    fetchConfig,
    fetchTenant
} from "../../../api/admin/config/endpoints-referrers";
import {
    registerFirebase,
    sendVerificationEmail as sendVerificationEmailAPI,
    sendPasswordReset as sendPasswordResetAPI
} from "../../../api/auth/endpoints-referrers";
import {
    fetchUser,
    updateUser,
    updateUserAgent,
    updateUserKB
} from "../../../api/users/endpoints-referrers";
import {
    fetchKnowledgeBase,
    fetchKnowledgeBases,
    fetchKnowledgeBaseConfig
} from "../../../api/admin/knowledge-base/endpoints-referrers";

function useFirebaseAuth() {
    const googleProvider = new GoogleAuthProvider();
    const microsoftProvider = new OAuthProvider("microsoft.com");
    const app = initializeApp(firebaseConfig);
    const auth = getAuth(app);
    const { initialize: initializeStatistics, reset: resetStatistics } =
        StatisticsContainer.useContainer();
    const [tenantInitialized, setTenantInitialized] = useState<boolean>(false);
    const [config, setConfig] = useState<Config>();
    const [version, setVersion] = useState<string>();
    const [isTrial, setIsTrial] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>();
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>();
    const [user, setUser] = useState<User | null>(null);
    const [email, setEmail] = useState<string>();
    const [emailVerified, setEmailVerified] = useState<boolean>();
    const [providerLoading, setProviderLoading] = useState<boolean>(false);
    // Super-admin support session (tenant_authentication_plan.md §2.5): read
    // from the ID token's claims after any sign-in so the banner survives a
    // reload. null = an ordinary session.
    const [supportSession, setSupportSession] = useState<{
        actor: string;
        expiresAt: Date;
    } | null>(null);
    const [sendVerificationWaitingTime, setSendVerificationWaitingTime] = useState<number>();
    const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase>();
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const [knowledgeBaseConfig, setKnowledgeBaseConfig] = useState<KBConfig>();
    const {
        currentAccessToken,
        isAuthenticated,
        isAuthenticatedState,
        setAccessToken,
        setTokens,
        clearToken,
        updateCurrentAccessToken
    } = useFirebaseToken(auth, useCallback(clearUser, []));
    const { timerStarted, timerTimeLeft, startTimer, stopTimer } = useTimer();

    // Initialize Firebase tenant ID
    useEffect(() => {
        // Pass the tenant origin explicitly (set by the auth-domain pages) so resolution does
        // not depend solely on the sessionStorage -> header round-trip, which can race.
        const storedTenantOrigin = sessionStorage.getItem("auth_tenant_origin") || undefined;
        fetchTenant(storedTenantOrigin)
            .then(response => {
                if (response.data?.tenantId) {
                    auth.tenantId = response.data.tenantId;
                }
            })
            .catch(error => {
                console.error("Failed to fetch tenant ID:", error);
            })
            .finally(() => {
                setTenantInitialized(true);
            });
    }, [auth]);

    useEffect(() => {
        setIsAdmin(
            !user
                ? undefined
                : user.role === UserRole.superAdmin ||
                      user.role === UserRole.admin ||
                      user.role === UserRole.manager
        );
        setIsSuperAdmin(!user ? undefined : user.role === UserRole.superAdmin);
    }, [user]);

    useEffect(() => {
        if (currentAccessToken && user) {
            initializeStatistics();
        } else {
            resetStatistics();
        }
    }, [currentAccessToken, user, initializeStatistics, resetStatistics]);

    useEffect(() => {
        fetchVersion().then(response => {
            setVersion(response.data);
        });
    }, []);

    useEffect(() => {
        if (user) {
            refreshConfig();
        } else {
            setConfig(undefined);
        }
    }, [user]);

    const refreshConfig = async () => {
        const response = await fetchConfig();
        setConfig(response.data);
    };

    useEffect(() => {
        if (user?.currentKnowledgeBaseId) {
            loadKnowledgeBaseConfig(user.companyId, user.currentKnowledgeBaseId);
        } else {
            setKnowledgeBaseConfig(undefined);
        }
    }, [user]);

    const loadKnowledgeBaseConfig = async (companyId: number, kbUid: string) => {
        const response = await fetchKnowledgeBaseConfig(companyId, kbUid);
        setKnowledgeBaseConfig(response.data || undefined);
    };

    const loadUser = useCallback(async () => {
        const {
            data: { ...user }
        } = await fetchUser();

        setUser(user);
        // Set emailVerified from backend user data (source of truth from Firebase claims)
        setEmailVerified(user.emailVerified);
        setEmail(user.email);
        await loadKnowledgeBases(user.companyId);
    }, []);

    const loadKnowledgeBases = async (companyId: number) => {
        const {
            data: { items }
        } = await fetchKnowledgeBases(companyId);
        setKnowledgeBases(items);
    };

    const loadKnowledgeBase = async (companyId: number, kbUid: string) => {
        const {
            data: { ...kb }
        } = await fetchKnowledgeBase(companyId, kbUid);
        // TODO will be returned
        setKnowledgeBase(kb);
        // setKnowledgeBase({ ...kb, expiringTrialPeriod: 0 });
        setIsTrial(kb.companyType === CompanyType.trial || kb.companyType === CompanyType.expired);

        // TODO will be deleted
        // const expiredDate = new Date().getTime() + 14 * 24 * 60 * 60 * 1000;
        // const createdDate = new Date(kb.createdAt).getTime();
        // const expiringTrialPeriod = Math.trunc(
        //     (expiredDate - createdDate) / (24 * 60 * 60 * 1000)
        // );
        // const companyType = !themeContainer.isTrial
        //     ? CompanyType.basic
        //     : expiringTrialPeriod > 0
        //       ? CompanyType.trial
        //       : CompanyType.expired;
        // setKnowledgeBase({ ...kb, companyType, expiringTrialPeriod });
        // setIsTrial(companyType === CompanyType.trial || companyType === CompanyType.expired);
    };

    const updateUserKnowledgeBase = async (kbId: string) => {
        const {
            data: { ...user }
        } = await updateUserKB(kbId);
        setUser(user);
    };

    // P3-08 "All assistants": bind (or clear) the explicit agent selection.
    const updateUserAgentSelection = async (agentUid: string | null) => {
        const {
            data: { ...user }
        } = await updateUserAgent(agentUid);
        setUser(user);
    };

    const refreshKnowledgeBase = useCallback(async () => {
        if (user?.currentKnowledgeBaseId) {
            loadKnowledgeBase(user.companyId, user.currentKnowledgeBaseId);
        } else {
            setKnowledgeBase(undefined);
        }
    }, [user]);

    useEffect(() => {
        refreshKnowledgeBase();
    }, [refreshKnowledgeBase, user]);

    const logout = useCallback(() => {
        signOut(auth).finally(async () => {
            await clearToken(true);
            stopTimer();
            setDataToLocalStorage(AuthEvents.LOGOUT, new Date().toISOString());
            removeDataToLocalStorage(AuthEvents.VERIFICATION_SENDING_TIME);
        });
    }, [auth, clearToken, stopTimer]);

    const loadUserData = useCallback(() => {
        // Always load user data from backend when authenticated
        // The backend will return emailVerified from Firebase token claims
        loadUser().catch(error => {
            // Don't logout on 422 (user not found) - this is expected during OAuth registration
            // The callback handler will register the user and call loadUser again
            if (isAxios(error, 422)) {
                return;
            }
            // Tenant auth policy refusal: the API interceptor already tore the
            // session down (or ignored it as a stale token). A logout() here
            // would sign out whatever session is being established instead —
            // e.g. a support session replacing a refused one.
            if (isAxios(error, 403) && error?.response?.data?.code) {
                return;
            }
            console.error("[loadUserData] loadUser failed, logging out:", error);
            logout();
        });
    }, [loadUser, logout]);

    useEffect(() => {
        if (isAuthenticated()) {
            // During an invite-driven registration visit, the registration flow owns
            // auth (it signs out any stale session and re-authenticates via the invite).
            // Auto-loading the user here would 401 against a deleted/not-yet-provisioned
            // user and bounce to /login, dropping the invite. Skip it.
            const params = new URLSearchParams(window.location.search);
            if (window.location.pathname.startsWith("/registration") && params.get("invite")) {
                return;
            }
            loadUserData();
        }
    }, [isAuthenticated, loadUserData]);

    // TODO will be returned
    // useEffect(() => {
    //     setIsTrial(user?.companyType === CompanyType.trial);
    // }, [user]);

    useEffect(() => {
        const handleStorageEventListener = async (event: WindowEventMap["storage"]) => {
            if (event.key === AuthEvents.LOGOUT) {
                await clearToken();
                stopTimer();
            } else if (
                (event.key === AuthEvents.LOGIN ||
                    event.key === AuthEvents.VERIFICATION_SENDING_TIME ||
                    event.key === AuthEvents.EMAIL_CONFIRMED_TIME) &&
                isAuthenticated()
            ) {
                location.reload();
            } else if (event.key === ACCESS_TOKEN_KEY && isAuthenticated()) {
                updateCurrentAccessToken();
            }
        };

        window.addEventListener("storage", handleStorageEventListener);

        return () => {
            window.removeEventListener("storage", handleStorageEventListener);
        };
    }, [isAuthenticated, clearToken, stopTimer, updateCurrentAccessToken]);

    useEffect(() => {
        setSendVerificationWaitingTime(timerStarted ? timerTimeLeft : undefined);
    }, [timerStarted, timerTimeLeft]);

    // Helper to wait for tenant initialization
    const waitForTenantInit = async () => {
        if (tenantInitialized) return;

        // Wait up to 5 seconds for tenant initialization
        const maxWait = 5000;
        const startTime = Date.now();

        while (!tenantInitialized && Date.now() - startTime < maxWait) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (!tenantInitialized) {
            console.warn("Tenant initialization timeout, proceeding without tenant ID");
        }
    };

    const signIn = async (email: string, password: string, invite: string | null) => {
        try {
            await waitForTenantInit();
            const response = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = response.user;
            const token = await firebaseUser.getIdToken();

            setAccessToken(token);
            setEmailVerified(firebaseUser.emailVerified);
            setEmail(firebaseUser.email ?? undefined);

            if (!firebaseUser.emailVerified) {
                try {
                    await sendVerification();
                } catch (verificationError: any) {
                    // The tenant's domain list is applied by the backend on
                    // this request: an unverified account outside it gets the
                    // domain message, not "verification email failed". The
                    // account already existed, so it is not deleted (unlike
                    // a refused sign-up).
                    if (
                        isAxios(verificationError, 403) &&
                        (verificationError as AxiosError<{ code?: string }>).response?.data
                            ?.code === "domain_not_allowed"
                    ) {
                        throw {
                            code: FirebaseErrorCode.domainNotAllowed,
                            originalError: verificationError
                        };
                    }
                    // Rate limited (TOO_MANY_ATTEMPTS_TRY_LATER): proceed to the
                    // email-confirm page, where the user can wait and resend.
                    const errorMsg = verificationError?.response?.data?.message || "";
                    if (!errorMsg.includes("TOO_MANY_ATTEMPTS_TRY_LATER")) {
                        console.error("[signIn] Verification email failed:", verificationError);
                        throw {
                            code: FirebaseErrorCode.verificationEmailFailed,
                            originalError: verificationError
                        };
                    }
                }
                // On login domain, return early after sending verification
                if (isAuthDomain()) {
                    return Promise.resolve(response);
                }
            } else {
                if (invite) {
                    await registerFirebaseUser(firebaseUser, email, invite);
                }

                // On login domain, return early without loading user data
                // The auth domain page will handle token extraction and redirect
                if (isAuthDomain()) {
                    setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });
                    return Promise.resolve(response);
                }

                await loadUser();
                setDataToLocalStorage(AuthEvents.LOGIN, new Date().toISOString());
            }

            setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });

            return Promise.resolve(response);
        } catch (error: any) {
            // if (error?.code === FirebaseErrorCode.manyRequests) {
            //     return Promise.resolve({});
            // } else {
            //     logout();
            //     return Promise.reject(error);
            // }
            logout();
            return Promise.reject(error);
        }
    };

    // Self-service company creation is retired (D7, 2026-09-25):
    // registration always requires a valid invite, so the old
    // companyName / forceCreateKB plumbing is gone from this chain.
    const signUp = async (
        email: string,
        password: string,
        invite: string | null,
        name?: string,
        position?: string
    ) => {
        try {
            await waitForTenantInit();
            const response = await createUserWithEmailAndPassword(auth, email, password);
            const firebaseUser = response.user;
            const token = await firebaseUser.getIdToken();

            setAccessToken(token);
            setEmailVerified(firebaseUser.emailVerified);
            setEmail(firebaseUser.email ?? undefined);

            // Phase 5 self-registration (D8): with no invite the backend creates
            // the User at the first verified login instead, so only the Firebase
            // account and the verification mail are made here.
            if (invite) {
                await registerFirebaseUser(firebaseUser, email, invite, name, position);
            }
            try {
                await sendVerification();
            } catch (sendError) {
                // The tenant policy (domain list) is applied by the backend on
                // this first request, after Firebase has already created the
                // account. Remove it again so a retry doesn't say "already in
                // use", and tell the page which rule refused it.
                const refusal = isAxios(sendError, 403)
                    ? (sendError as AxiosError<{ code?: string }>).response?.data?.code
                    : undefined;
                if (refusal === "domain_not_allowed") {
                    await deleteUser(firebaseUser).catch(deleteError =>
                        console.warn("[signUp] Could not remove refused account:", deleteError)
                    );
                    throw { code: FirebaseErrorCode.domainNotAllowed, originalError: sendError };
                }
                throw sendError;
            }
            setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });

            return Promise.resolve(response);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    // Block OAuth when a tenant is expected (auth/login domain carries a tenant origin) but the
    // Firebase tenant was not resolved. Without this, signInWithPopup runs against the default
    // user pool and mints a tenant-less user that the backend cannot register.
    const ensureTenantResolved = () => {
        if (sessionStorage.getItem("auth_tenant_origin") && !auth.tenantId) {
            throw new Error("Tenant could not be resolved. Please refresh the page and try again.");
        }
    };

    /** Backend policy code → the Firebase-style code the pages switch on. */
    /**
     * Login domain, after any sign-in popup: have the backend apply the
     * tenant policy (method, domain, directory, password expiry) to the new
     * token now, while the SDK session is still here, instead of letting the
     * tenant's callback page discover the refusal. A refused FIRST sign-in
     * has just created a Firebase account nobody asked for — delete it
     * again, like a domain-refused password sign-up. An existing account is
     * left alone: the refusal may be a policy change the admin will revisit.
     */
    const precheckTenantPolicy = async (response: UserCredential) => {
        try {
            await precheckSignIn(await response.user.getIdToken());
        } catch (error: any) {
            const refusal = toPolicyRefusalError(error);
            if (!refusal) throw error;
            await discardRefusedAccount(response);
            throw refusal;
        }
    };

    /** Delete the Firebase account a refused popup sign-in has just created. */
    const discardRefusedAccount = async (response: UserCredential) => {
        if (!isRefusedFirstSignIn(response)) return;
        await deleteUser(response.user).catch(deleteError =>
            console.warn("[precheck] Could not remove refused account:", deleteError)
        );
    };

    const signInWithGoogle = async (invite: string | null) => {
        try {
            // Check tenant is initialized synchronously to avoid popup blocker
            if (!tenantInitialized) {
                throw new Error("Tenant not initialized. Please wait and try again.");
            }
            ensureTenantResolved();
            const response = await signInWithPopup(auth, googleProvider);

            // On login domain, return the credential without API calls
            // The auth domain page will handle token extraction and redirect
            if (isAuthDomain()) {
                await precheckTenantPolicy(response);
                return Promise.resolve(response);
            }

            return await signInWithProvider(response, invite);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    const signInWithMicrosoft = async (invite: string | null) => {
        try {
            // Check tenant is initialized synchronously to avoid popup blocker
            if (!tenantInitialized) {
                throw new Error("Tenant not initialized. Please wait and try again.");
            }
            ensureTenantResolved();
            const response = await signInWithPopup(auth, microsoftProvider);
            await confirmDirectory(response);

            // On login domain, return the credential without API calls
            // The auth domain page will handle token extraction and redirect
            if (isAuthDomain()) {
                await precheckTenantPolicy(response);
                return Promise.resolve(response);
            }

            return await signInWithProvider(response, invite);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    /**
     * Main-app SSO (tenant_authentication_plan.md Phase 4): sign in with one
     * of the tenant's SAML/OIDC providers, exactly like Google/Microsoft —
     * a tenant-scoped popup on the login domain, then the shared provider
     * path. One case is translated: when the IdP returns an email that
     * already exists as a password account on the tenant, Firebase refuses
     * with account-exists-with-different-credential and hands back the
     * pending SSO credential; the page then asks for the password once and
     * calls linkSsoCredential, so the user keeps their account instead of
     * getting a second one (plan §5, SSO/password collision).
     */
    const signInWithSso = async (provider: SsoProviderOption, invite: string | null) => {
        try {
            if (!tenantInitialized) {
                throw new Error("Tenant not initialized. Please wait and try again.");
            }
            ensureTenantResolved();
            let response: UserCredential;
            try {
                response = await signInWithPopup(auth, ssoAuthProvider(provider));
            } catch (error: any) {
                if (error?.code === FirebaseErrorCode.invalidProvider) {
                    const pending =
                        provider.providerType === "saml"
                            ? SAMLAuthProvider.credentialFromError(error)
                            : OAuthProvider.credentialFromError(error);
                    // Firebase lists the account's providers unless email
                    // enumeration protection hides them; with an empty list
                    // the page still offers the password step but says it may
                    // not apply (passwordKnown: false).
                    const existing: string[] =
                        error?.customData?._tokenResponse?.verifiedProvider ?? [];
                    if (pending && (existing.length === 0 || existing.includes("password"))) {
                        throw {
                            code: FirebaseErrorCode.ssoLinkRequired,
                            email: error?.customData?.email as string | undefined,
                            pending,
                            provider,
                            passwordKnown: existing.includes("password")
                        };
                    }
                }
                throw error;
            }
            if (isAuthDomain()) {
                await precheckTenantPolicy(response);
                return Promise.resolve(response);
            }
            return await signInWithProvider(response, invite);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    /**
     * Second half of the collision case above: prove the password account,
     * attach the pending SSO credential to it, then sign in with the SSO
     * provider again so the session's token names the SSO provider (the
     * tenant may not allow password tokens at all). The IdP session is still
     * warm, so the second popup usually closes by itself.
     */
    const linkSsoCredential = async (
        provider: SsoProviderOption,
        userEmail: string,
        password: string,
        pending: AuthCredential,
        invite: string | null
    ) => {
        try {
            await waitForTenantInit();
            const passwordSession = await signInWithEmailAndPassword(auth, userEmail, password);
            await linkWithCredential(passwordSession.user, pending);
            const response = await signInWithPopup(auth, ssoAuthProvider(provider));
            if (isAuthDomain()) {
                await precheckTenantPolicy(response);
                return Promise.resolve(response);
            }
            return await signInWithProvider(response, invite);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    /**
     * Microsoft directory pinning (tenant_authentication_plan.md §2.6). The
     * popup leaves us holding Microsoft's own ID token; the backend verifies
     * it, checks the account's directory against the tenant's list and
     * records it as a claim. The Firebase token is then refreshed so every
     * later request (and the callback URL on the login domain) carries the
     * claim. Tenants without pinning answer { pinned: false } and nothing
     * changes. A refusal becomes a sign-in error; the popup's session is
     * discarded by the caller's logout(), and a first-time account with it.
     */
    const confirmDirectory = async (response: UserCredential) => {
        const microsoftIdToken = OAuthProvider.credentialFromResult(response)?.idToken;
        if (!microsoftIdToken) {
            // No Microsoft token in the result (cannot happen for a popup sign-in);
            // let the backend decide — it refuses pinned tenants on the next request.
            console.warn("[confirmDirectory] No Microsoft ID token in the popup result");
            return;
        }
        const firebaseIdToken = await response.user.getIdToken();
        try {
            const { data } = await confirmMicrosoftDirectory({ firebaseIdToken, microsoftIdToken });
            if (data?.pinned) {
                await response.user.getIdToken(true);
            }
        } catch (error: any) {
            if (error?.response?.data?.code === "directory_not_allowed") {
                await discardRefusedAccount(response);
                throw { code: FirebaseErrorCode.directoryNotAllowed, originalError: error };
            }
            throw error;
        }
    };

    const signInWithProvider = async (response: UserCredential, invite: string | null) => {
        try {
            setProviderLoading(true);

            const firebaseUser = response.user;
            const token = await firebaseUser.getIdToken();
            setAccessToken(token);

            if (!firebaseUser.email) {
                throw { code: FirebaseErrorCode.invalidEmail };
            }

            setEmail(firebaseUser.email);
            setEmailVerified(firebaseUser.emailVerified || !!firebaseUser.providerId);
            await getOrRegisterUser(firebaseUser, firebaseUser.email, invite);

            setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });
            setDataToLocalStorage(AuthEvents.LOGIN, new Date().toISOString());

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        } finally {
            setProviderLoading(false);
        }
    };

    /**
     * Support session (plan §2.5): redeem the single-use code the super admin's
     * browser carried here, sign in to THIS tenant with the returned custom
     * token, and load the tenant's support user. The code is the credential;
     * a reload of the landing page fails on purpose.
     */
    const signInWithSupportSession = async (code: string) => {
        // Redeem first: an invalid or expired link must not cost the
        // person whatever session this browser already holds.
        const { data } = await redeemSupportSession(code);
        if (!data?.customToken || !data?.tenantId) {
            throw new Error("Support session could not be started");
        }
        // Then that session (the super admin's own, a stale one the
        // tenant's policy now refuses, or a previous support session) must
        // be gone BEFORE the custom-token sign-in: it signs in a different
        // Firebase user, and a late teardown of the old session would take
        // the new one with it. Local clear only — a server logout with a
        // token the tenant refuses would 403.
        await signOut(auth).catch(() => undefined);
        await clearToken(false);
        auth.tenantId = data.tenantId;
        const response = await signInWithCustomToken(auth, data.customToken);
        const token = await response.user.getIdToken();
        setAccessToken(token);
        setEmail(response.user.email ?? undefined);
        setEmailVerified(true);
        await loadUser();
        setTokens({ accessToken: token, refreshToken: response.user.refreshToken });
        setDataToLocalStorage(AuthEvents.LOGIN, new Date().toISOString());
        return response;
    };

    // Keep the support banner in step with the signed-in Firebase user.
    useEffect(() => {
        const unsubscribe = auth.onIdTokenChanged(async firebaseUser => {
            if (!firebaseUser) {
                setSupportSession(null);
                return;
            }
            try {
                const { claims } = await firebaseUser.getIdTokenResult();
                const exp = Number(claims.mbaSupportExp);
                setSupportSession(
                    claims.mbaSupport === true && Number.isFinite(exp)
                        ? {
                              actor: String(claims.mbaSupportActor ?? "unknown"),
                              expiresAt: new Date(exp * 1000)
                          }
                        : null
                );
            } catch {
                setSupportSession(null);
            }
        });
        return unsubscribe;
    }, [auth]);

    const getOrRegisterUser = async (user: FirebaseUser, email: string, invite: string | null) => {
        // D7: no invite, no registration — the trial-host no-invite branch
        // is gone with self-service signup.
        if (invite) {
            await registerFirebaseUser(user, email, invite);
            await loadUser();
        } else {
            try {
                await loadUser();
            } catch (error) {
                if (error instanceof AxiosError && error.response?.status === 422) {
                    await deleteUser(user);
                    throw { code: FirebaseErrorCode.invalidInvite };
                }
                throw error;
            }
        }
    };

    const registerFirebaseUser = async (
        user: FirebaseUser,
        email: string,
        invite: string | null,
        name?: string,
        position?: string
    ) => {
        const signupData = {
            email,
            from: invite,
            firebaseId: user.uid,
            firstName: name,
            position
        };
        try {
            await registerFirebase(signupData);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 409) {
                await updateUser(user.uid);
            } else {
                await clearNonexistentUser(user);
                throw isAxios(error, 403, "Invalid invite")
                    ? { code: FirebaseErrorCode.invalidInvite }
                    : isAxios(error, 403, "Invalid email format")
                      ? { code: FirebaseErrorCode.invalidEmail }
                      : isAxios(error, 403, "Database change denied")
                        ? { code: FirebaseErrorCode.forbiddenInvite }
                        : isAxios(error, 403, "Invite link does not match your email")
                          ? { code: FirebaseErrorCode.inappropriateInvite }
                          : error;
            }
        }
    };

    const clearNonexistentUser = async (user: FirebaseUser) => {
        try {
            await fetchUser();
        } catch (userError) {
            if (userError instanceof AxiosError && userError.response?.status === 422) {
                await deleteUser(user);
            }
        }
    };

    const sendVerification = async () => {
        try {
            setDataToLocalStorage(AuthEvents.VERIFICATION_SENDING_TIME, new Date().toISOString());
            startTimer(60);

            // Always use backend endpoint which uses Firebase Admin SDK
            const loginUrl = process.env.REACT_APP_LOGIN_DOMAIN;
            if (!loginUrl) {
                throw new Error("REACT_APP_LOGIN_DOMAIN environment variable not set");
            }
            await sendVerificationEmailAPI(loginUrl);

            return Promise.resolve(true);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    /**
     * Phase 3: change the signed-in user's password. Re-authenticates with
     * the current password first (so it works on a tenant subdomain, where
     * the SDK may hold no session after an auth-domain login, and satisfies
     * Firebase's recent-login rule), then updates it — Firebase applies the
     * tenant's password policy — and refreshes the stored tokens. The backend
     * notices the change through Firebase's tokensValidAfterTime, so no API
     * call is needed for an expired password to become current again.
     */
    const changePassword = async (
        userEmail: string,
        currentPassword: string,
        newPassword: string
    ) => {
        await waitForTenantInit();
        const credential = await signInWithEmailAndPassword(auth, userEmail, currentPassword);
        await updatePassword(credential.user, newPassword);
        const token = await credential.user.getIdToken(true);
        setAccessToken(token);
        setEmail(credential.user.email ?? undefined);
        setTokens({ accessToken: token, refreshToken: credential.user.refreshToken });
        if (!isAuthDomain()) {
            await loadUser();
        }
    };

    const sendPasswordReset = async (
        email: string,
        _returnUrl?: string,
        _tenantOrigin?: string
    ) => {
        try {
            // Always use backend endpoint which uses Firebase Admin SDK
            const loginUrl = process.env.REACT_APP_LOGIN_DOMAIN;
            if (!loginUrl) {
                throw new Error("REACT_APP_LOGIN_DOMAIN environment variable not set");
            }
            await sendPasswordResetAPI(email, loginUrl);

            return Promise.resolve(true);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    function clearUser() {
        setUser(null);
        setEmail(undefined);
        setEmailVerified(undefined);
    }

    /**
     * Provisions an OAuth user in the backend after redirect from login domain
     * Should be called on tenant domain after tokens are set
     * @param email - User's email from OAuth
     * @param invite - Optional invite code
     * @param firebaseUid - Firebase user ID
     * @returns Promise that resolves when user is provisioned
     */
    const provisionOAuthUser = async (
        email: string,
        invite: string | null,
        firebaseUid: string
    ): Promise<void> => {
        setEmail(email);
        setEmailVerified(true); // OAuth users are always email verified

        // Call backend to register/provision the user. D7: no invite, no
        // registration — self-service company creation is retired.
        if (invite) {
            const signupData = {
                email,
                from: invite,
                firebaseId: firebaseUid,
                firstName: undefined,
                position: undefined
            };

            try {
                await registerFirebase(signupData);
            } catch (error) {
                if (error instanceof AxiosError && error.response?.status === 409) {
                    await updateUser(firebaseUid);
                } else {
                    throw error;
                }
            }
        }

        // Store auth event
        setDataToLocalStorage(AuthEvents.LOGIN, new Date().toISOString());
    };

    // Debug helper for testing auth flows
    // Enable with REACT_APP_AUTH_DEBUG=true in your .env file
    useEffect(() => {
        if (process.env.REACT_APP_AUTH_DEBUG === "true") {
            (window as any).__debugAuth = {
                // Test token REFRESH via REST API
                // Corrupts access token but keeps refresh token valid
                // Expected: 401 → REST API refresh → retry → success
                simulateRefreshableToken: () => {
                    localStorage.setItem("accessToken", "invalid_expired_token");
                },
                // Test NO refresh possible: invalid BOTH tokens
                // Expected: 401 → REST API fails → logout → redirect to login
                simulateExpiredToken: () => {
                    localStorage.setItem("accessToken", "invalid_expired_token");
                    localStorage.setItem("refreshToken", "invalid_refresh_token");
                },
                // Full logout (clears everything)
                fullLogout: () => logout(),
                // Check current auth state
                getState: () => ({
                    hasFirebaseUser: !!auth.currentUser,
                    firebaseUid: auth.currentUser?.uid,
                    hasLocalStorageTokens: isAuthenticated(),
                    isAuthenticatedState,
                    user: user?.email
                }),
                // Force clear tokens (simulates token cleared by another tab)
                forceClearTokens: () => clearToken()
            };
        }
        return () => {
            if (process.env.REACT_APP_AUTH_DEBUG === "true") {
                delete (window as any).__debugAuth;
            }
        };
    }, [auth, isAuthenticated, isAuthenticatedState, user, logout, clearToken]);

    // Tenant admin feature gate (tenant_admin_permissions_design.md §6):
    // super admins bypass; company admins need the tenant's resolved
    // permission (delivered on the login payload). Managers and users never
    // gain a feature through it.
    const hasAdminFeature = useCallback(
        (feature: string): boolean => {
            if (isSuperAdmin) return true;
            if (user?.role !== UserRole.admin) return false;
            return !!user?.adminFeatures?.[feature];
        },
        [isSuperAdmin, user]
    );

    return {
        isAuthenticated,
        isAuthenticatedState,
        currentAccessToken,
        user,
        isSuperAdmin,
        isAdmin,
        hasAdminFeature,
        isTrial,
        email,
        emailVerified,
        sendVerificationWaitingTime,
        knowledgeBase,
        knowledgeBases,
        knowledgeBaseConfig,
        config,
        version,
        providerLoading,
        supportSession,
        refreshConfig,
        loadUser,
        updateUserKnowledgeBase,
        updateUserAgentSelection,
        refreshKnowledgeBase,
        loadKnowledgeBaseConfig,
        loadKnowledgeBases,
        setUser,
        setTokens,
        setEmail,
        setEmailVerified,
        signIn,
        signUp,
        signInWithGoogle,
        signInWithMicrosoft,
        signInWithSso,
        linkSsoCredential,
        signInWithSupportSession,
        sendVerification,
        sendPasswordReset,
        changePassword,
        logout,
        provisionOAuthUser
    };
}

/** Firebase provider object for one of the tenant's main-app SSO providers. */
export const ssoAuthProvider = (provider: SsoProviderOption) =>
    provider.providerType === "saml"
        ? new SAMLAuthProvider(provider.firebaseProviderId)
        : new OAuthProvider(provider.firebaseProviderId);

export const FirebaseAuthContainer = createContainer(useFirebaseAuth);
